// performance-events.ts
//
// Timeline events layered onto the Performance longitudinal charts (pins +
// annotation cards) and the forward projection.
//
// These are NOT invented data points. Each event is derived from material the
// client has already been shown:
//   - Work events come from the real Deep Signal Blueprint waves
//     (resolveCustomerContentByBrand), so the roadmap on the chart is the same
//     plan as the Blueprint hand-off card.
//   - Measurement events come from the measurement methodology we proposed in
//     the deck: re-measure on a fixed cadence after Wave 1 deploys (velocity
//     reads first, weight reads later).
//
// The only thing typed by hand here is the planned Wave 1 deploy date (the
// anchor the cadence keys off) and the cadence offsets themselves, both of
// which are editable constants below. No metric values live here; the projected
// trajectory is computed in the chart from the real baseline point.

import { resolveCustomerContentByBrand } from "@/app/components/deep-signal/deep-signal-content";

export type PerformanceEventType = "content" | "optimization" | "scan" | "launch";

export interface PerformanceEvent {
  id:          string;
  date:        string;            // ISO date, e.g. "2026-06-30"
  title:       string;
  description: string;
  type:        PerformanceEventType;
}

// Measurement methodology (from the proposal deck). After Wave 1 deploys we
// re-measure on this cadence, in days. Velocity signals (Perplexity, live web)
// register first; weight signals (ChatGPT, Claude, Gemini, training data)
// follow over the later reads. Editable.
export const MEASUREMENT_OFFSETS_DAYS = [15, 30, 60, 90];

// Projected lift per scheduled run, as a cumulative multiplier on the real
// baseline value. ILLUSTRATIVE, not measured. This is the single editable seam
// for the forward projection: edit it here, never in the chart component.
// Replace with a real model or backend-provided values later.
export const PROJECTION_RAMP = [1.06, 1.14, 1.26, 1.40];

const MEASUREMENT_META: Record<number, { title: string; description: string }> = {
  15: { title: "Measurement run · D15", description: "First velocity read. Confirms Perplexity is responding to the deployed evidence packages." },
  30: { title: "Measurement run · D30", description: "Velocity confirmed. First retrieval-augmented read on ChatGPT." },
  60: { title: "Measurement run · D60", description: "Weight window opens. Encoding movement in ChatGPT and Claude." },
  90: { title: "Deep run · D90",        description: "Full competitive picture. ROI tracking against the locked baseline begins." },
};

// Planned Wave 1 deploy date per brand. This is the one date the whole roadmap
// keys off; set it to the real planned deploy. Editable.
//
// EMPTY on purpose (7/28): the June 30 Sunbit date was the planned roadmap from
// the proposal phase and reality diverged (content shipped mid-July, re-measure
// ran 7/27), so the pins it generated read as stale fiction on prod. With no
// entries, getPerformanceEvents returns [] for every brand, the event pins
// disappear, and the chart hides its Events control until real dates come from
// the Blueprint. Re-add a brand here only with a real, agreed deploy date.
const WAVE1_DEPLOY: Record<string, string> = {};

// Deploy offset (days from Wave 1 deploy) for each Blueprint wave, in order.
// Mirrors the Blueprint velocity_labels: Wave 1 now, Wave 2 a few weeks on,
// Wave 3 a couple of months on. Editable.
const WAVE_DEPLOY_OFFSET_DAYS = [0, 21, 49];

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function brandKey(brandName?: string): string | null {
  if (!brandName) return null;
  // Exact match only: a brand that merely contains a managed client's name must
  // never inherit that client's roadmap.
  const q = brandName.trim().toLowerCase();
  return Object.keys(WAVE1_DEPLOY).find((k) => q === k) ?? null;
}

// Return the roadmap events for a brand: Blueprint wave deploys + scheduled
// measurement runs. Empty for any brand without a configured deploy date, so
// the event layer is present and ready but never shows invented events.
export function getPerformanceEvents(brandName?: string): PerformanceEvent[] {
  const key = brandKey(brandName);
  if (!key) return [];

  const deploy = WAVE1_DEPLOY[key];
  const { blueprint } = resolveCustomerContentByBrand(brandName);
  const events: PerformanceEvent[] = [];

  // Work events: one per real Blueprint wave.
  (blueprint?.waves ?? []).forEach((w, i) => {
    const offset = WAVE_DEPLOY_OFFSET_DAYS[i] ?? WAVE_DEPLOY_OFFSET_DAYS[WAVE_DEPLOY_OFFSET_DAYS.length - 1] + (i - WAVE_DEPLOY_OFFSET_DAYS.length + 1) * 30;
    events.push({
      id:          `wave-${w.number}`,
      date:        addDays(deploy, offset),
      title:       `Wave ${w.number}: ${w.title}`,
      description: `${w.subtitle} · ${w.velocity_label}`,
      type:        "launch",
    });
  });

  // Measurement events: scheduled re-measurements on the methodology cadence.
  MEASUREMENT_OFFSETS_DAYS.forEach((n) => {
    const meta = MEASUREMENT_META[n];
    events.push({
      id:          `run-d${n}`,
      date:        addDays(deploy, n),
      title:       meta?.title ?? `Measurement run · D${n}`,
      description: meta?.description ?? "Scheduled Deep Signal re-measurement.",
      type:        "scan",
    });
  });

  return events.sort((a, b) => a.date.localeCompare(b.date));
}
